/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react'
import { RegItem } from 'src/types/regItem'
import { NavLink, RightDrawer } from 'baby-design'
import NiceModal, { NiceModalHocProps, useModal } from '@ebay/nice-modal-react'
import { useRegItemReservations } from 'src/routes/(registry)/api/reservations'
import { RegItemFields } from './fields/RegItemFields'
import { FavorFields } from './fields/FavorFields'
import { ExternalRegistryFields } from './fields/ExternalRegistryFields'
import { GiftCardFields } from './fields/GiftCardFields'
import { CrowdfundFields } from './fields/CrowdfundFields'
import { GroupGiftFields } from './fields/GroupGiftFields'

import ConfirmRegItemDeleteModal from '../ConfirmRegItemDeleteModal/ConfirmRegItemDeleteModal'
import GroupGiftRedemptionModal from '../GroupGiftRedemptionModal/GroupGiftRedemptionModal'
import { useRegistry } from '../../utils/useQueries'
import { getCategoriesAsKeyValue } from '../../utils/productHelpers'
import useSaveRegItem from '../../hooks/useSaveRegItem'
import useGroupGiftUnredeemedFundsModal from '../../hooks/useGroupGiftUnredeemedFundsModal'

import css from './EditRegItemDrawer.styles.scss'

interface EditRegItemDrawerProps {
  regItem: RegItem
}

export default NiceModal.create(({ regItem: initialRegItem }) => {
  const modal = useModal()
  const [regItem, setRegItem] = useState(initialRegItem)
  const { openUnredeemedFundsModal } = useGroupGiftUnredeemedFundsModal(regItem)

  const {
    saveRegItemMutation,
    onSave,
    regItemChanges,
    setRegItemChanges,
    handleClose,
    hasUnsavedChanges,
    clearRegItemFields,
    updateRegItemFields,
  } = useSaveRegItem({ regItem, modal })

  const { data: reservations } = useRegItemReservations(regItem.id)

  const categories = getCategoriesAsKeyValue(useRegistry()?.categories || [])

  const regItemFields: RegItem = {
    ...regItem,
    ...regItemChanges,
  }

  useEffect(() => {
    setRegItemChanges({})
  }, [modal.visible])

  useEffect(() => {
    setRegItem(initialRegItem)
  }, [initialRegItem])

  const handleUpdateRegItem = (fields: Record<string, any>) => {
    setRegItem({
      ...regItem,
      ...fields,
    })
  }

  const handleDelete = async () => {
    if (regItem.isGroupGift) {
      const confirmGroupGiftRedemption: boolean | undefined =
        await NiceModal.show(GroupGiftRedemptionModal, {
          regItem,
          action: 'delete-reg-item',
        })
      if (confirmGroupGiftRedemption) {
        modal.hide()
      }
      return
    }

    const confirmDeleteModal: boolean = await NiceModal.show(
      ConfirmRegItemDeleteModal,
      { regItem }
    )
    if (confirmDeleteModal) {
      modal.hide()
    }
  }

  const toastImageOverride = (
    <div className={css.EditRegItemDrawer__atcToastImageContainer}>
      <img
        alt={regItem.title}
        className={css.EditRegItemDrawer__atcToastImage}
        src={regItem.imgUrl}
      />
    </div>
  )

  let Fields = RegItemFields
  if (regItem.isFavor) {
    Fields = FavorFields
  } else if (regItem.isExternalRegistry) {
    Fields = ExternalRegistryFields
  } else if (regItem.isGiftCard) {
    Fields = GiftCardFields
  } else if (regItem.isCrowdfund) {
    Fields = CrowdfundFields
  } else if (regItem.isGroupGift) {
    Fields = GroupGiftFields
  }

  return (
    <RightDrawer
      contextualIcon={
        <NavLink
          disabled={saveRegItemMutation.isLoading || !hasUnsavedChanges()}
          size="md"
          variant="eggplant"
          onClick={onSave}
        >
          Save
        </NavLink>
      }
      handleClose={handleClose}
      handleDismount={modal.remove}
      isOpen={modal.visible}
      title="Edit Item Details"
    >
      <Fields
        buyingOptionsProps={{
          unredeemedFundsCallback: openUnredeemedFundsModal,
          onAddToCartSuccess: modal.hide,
          toastImageOverride,
        }}
        categories={categories}
        clearRegItemFields={clearRegItemFields}
        handleDelete={handleDelete}
        regItem={regItem}
        regItemFields={regItemFields}
        reservations={reservations || []}
        updateRegItemFields={updateRegItemFields}
        onRegItemUpdated={handleUpdateRegItem}
      />
    </RightDrawer>
  )
}) as React.FC<EditRegItemDrawerProps & NiceModalHocProps>
